import { validateLead, hasErrors } from './validation';
import {
  sanitizeLeadType,
  sanitizeLeadSource,
  applyAgedExclusivity,
  leadTypesForChannel,
} from './leadMeta';

const LEAD_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'lead_type',
  'lead_source',
  'notes',
];

/** Collapse header text for alias matching */
function headerKey(raw) {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[\s-]+/g, '');
}

const HEADER_ALIASES = {
  email: 'email',
  firstname: 'first_name',
  fname: 'first_name',
  first: 'first_name',
  lastname: 'last_name',
  lname: 'last_name',
  last: 'last_name',
  phonenumber: 'phone',
  mobile: 'phone',
  cellphone: 'phone',
  tel: 'phone',
  emailaddress: 'email',
  mail: 'email',
  leadtype: 'lead_type',
  type: 'lead_type',
  leadsource: 'lead_source',
  source: 'lead_source',
  notes: 'notes',
  comments: 'notes',
  remark: 'notes',
};

// AgedLeadStore-specific header → internal field mapping
const AGED_LEAD_STORE_HEADERS = {
  lead_id: 'lead_vendor_id',
  first_name: 'first_name',
  last_name: 'last_name',
  applicant_name: '_ignore',
  address: 'address',
  city: 'city',
  state: 'state',
  zip_plus_four: 'zip',
  area_code: 'area_code',
  time_zone: 'time_zone',
  landline: 'landline',
  cell_phone: 'cell_phone',
  recent_landline_1: 'recent_landline_1',
  recent_cell_phone_1: 'recent_cell_phone_1',
  recent_cell_phone_2: 'recent_cell_phone_2',
  recent_cell_phone_3: 'recent_cell_phone_3',
  email: 'email',
  recent_email_1: 'recent_email_1',
  recent_email_2: 'recent_email_2',
  email_combo: 'email_combo',
  cell_combo: 'cell_combo',
  landline_cell_combo: 'landline_cell_combo',
  date_requested: 'date_requested',
  ip_address: 'ip_address',
  dob: 'dob',
  gender: 'gender',
  marital_status: 'marital_status',
  smoker: 'smoker',
  height_feet: 'height_feet',
  height_inches: 'height_inches',
  weight: 'weight',
  coverage_amount: 'coverage_amount_requested',
  policy_type: 'policy_type_requested',
  term: 'term_requested',
  jornaya_lead_id: 'jornaya_lead_id',
  trusted_form: 'trusted_form',
};

const AGED_LEAD_STORE_DETECTION_HEADERS = new Set([
  'jornaya_lead_id',
  'trusted_form',
  'lead_id',
]);

/**
 * RFC-style CSV parse: commas, quoted fields, doubled quotes, newlines in quotes.
 */
export function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const s = String(text ?? '')
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  const pushField = () => {
    row.push(field);
    field = '';
  };
  const pushRow = () => {
    if (row.length > 1 || row.some((c) => String(c).length > 0)) {
      rows.push(row);
    }
    row = [];
  };

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      pushField();
    } else if (c === '\n') {
      pushField();
      pushRow();
    } else {
      field += c;
    }
  }
  pushField();
  if (row.length) pushRow();

  return rows;
}

function resolveHeaderField(cell) {
  const raw = String(cell ?? '').trim();
  if (!raw) return null;
  const k = headerKey(raw);
  const underscored = raw.trim().toLowerCase().replace(/\s+/g, '_');
  if (LEAD_FIELDS.includes(underscored)) return underscored;
  return HEADER_ALIASES[k] || null;
}

/**
 * @returns {{ fieldToCol: Record<string, number>, unknownHeaders: string[] }}
 */
export function mapCsvHeaders(headerCells) {
  const fieldToCol = {};
  const unknownHeaders = [];
  headerCells.forEach((cell, idx) => {
    const field = resolveHeaderField(cell);
    if (field) {
      if (fieldToCol[field] === undefined) fieldToCol[field] = idx;
    } else if (String(cell ?? '').trim()) {
      unknownHeaders.push(String(cell).trim());
    }
  });
  return { fieldToCol, unknownHeaders };
}

/**
 * Detect if headers match AgedLeadStore format.
 */
export function detectAgedLeadStoreFormat(headerCells) {
  const normalized = headerCells.map((h) =>
    String(h ?? '').trim().toLowerCase().replace(/\s+/g, '_')
  );
  return normalized.some((h) => AGED_LEAD_STORE_DETECTION_HEADERS.has(h));
}

/**
 * Map headers for AgedLeadStore format.
 * @returns {{ fieldToCol: Record<string, number>, unknownHeaders: string[] }}
 */
export function mapAgedLeadStoreHeaders(headerCells) {
  const fieldToCol = {};
  const unknownHeaders = [];
  headerCells.forEach((cell, idx) => {
    const normalized = String(cell ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    const mapped = AGED_LEAD_STORE_HEADERS[normalized];
    if (mapped && mapped !== '_ignore') {
      if (fieldToCol[mapped] === undefined) fieldToCol[mapped] = idx;
    } else if (!mapped && String(cell ?? '').trim()) {
      unknownHeaders.push(String(cell).trim());
    }
  });
  return { fieldToCol, unknownHeaders };
}

// ProsperityMentors-specific header → internal field mapping
const PROSPERITY_MENTORS_HEADERS = {
  name: 'name',
  email: 'email',
  phone: 'phone',
  channel: 'channel',
  status: 'status',
  payment_status: 'payment_status',
  has_account: 'has_account',
  captured_date: 'captured_date',
  source: 'source',
  event_title: 'event_title',
  agent_name: 'agent_name',
  agent_email: 'agent_email',
  activities: 'activities',
  form_responses: 'form_responses',
  quiz_responses: 'quiz_responses',
  survey_responses: 'survey_responses',
};

/**
 * Attach batch import metadata (import_batch_id, import_label, imported_at) to a lead.
 * imported_at is only set from the session value when the row does not already carry one
 * (e.g. ProsperityMentors captured_date overrides the session timestamp).
 */
function attachImportMeta(lead, importMeta) {
  if (!importMeta) return lead;
  const { import_batch_id, import_label, imported_at } = importMeta;
  if (import_batch_id !== undefined) lead.import_batch_id = import_batch_id;
  if (import_label !== undefined) lead.import_label = import_label;
  if (imported_at !== undefined && !lead.imported_at) lead.imported_at = imported_at;
  return lead;
}

function parseBooleanField(value) {
  const v = String(value ?? '').trim().toLowerCase();
  if (['yes', 'y', '1', 'true'].includes(v)) return true;
  if (['no', 'n', '0', 'false'].includes(v)) return false;
  return null;
}

/** ProsperityMentors-style boolean: only "true"/"yes"/"1" → true, everything else → false. */
function parsePmBoolean(value) {
  const v = String(value ?? '').trim().toLowerCase();
  return v === 'true' || v === 'yes' || v === '1';
}

function parseIntField(value) {
  const v = String(value ?? '').trim().replace(/[^\d]/g, '');
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

function parseWeight(value) {
  const v = String(value ?? '').trim().toLowerCase().replace(/lbs?/g, '').trim();
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

/**
 * Map a single row to a lead using AgedLeadStore field mapping.
 */
export function mapAgedLeadStoreRowToLead(cells, fieldToCol, channel = 'LD', importMeta = null) {
  const get = (field) => {
    const col = fieldToCol[field];
    if (col === undefined) return '';
    return String(cells[col] ?? '').trim();
  };

  const cellPhone = get('cell_phone');
  const landline = get('landline');

  // Phone assignment: cell_phone is primary, landline becomes secondary
  let phone = cellPhone || landline || '';
  let phoneSecondary = '';
  if (cellPhone && landline) {
    phoneSecondary = landline;
  }

  // Build recent_phones array from recent phone fields
  const recentPhones = [
    get('recent_landline_1'),
    get('recent_cell_phone_1'),
    get('recent_cell_phone_2'),
    get('recent_cell_phone_3'),
  ].filter(Boolean);

  // Build recent_emails array
  const recentEmails = [
    get('recent_email_1'),
    get('recent_email_2'),
  ].filter(Boolean);

  // Build metadata
  const metadata = {};
  if (recentPhones.length) metadata.recent_phones = recentPhones;
  if (recentEmails.length) metadata.recent_emails = recentEmails;
  if (get('email_combo')) metadata.email_combo = get('email_combo');
  if (get('cell_combo')) metadata.cell_combo = get('cell_combo');
  if (get('landline_cell_combo')) metadata.landline_cell_combo = get('landline_cell_combo');
  if (get('ip_address')) metadata.ip_address = get('ip_address');
  if (get('jornaya_lead_id')) metadata.jornaya_lead_id = get('jornaya_lead_id');
  if (get('trusted_form')) metadata.trusted_form = get('trusted_form');

  // Parse date_requested as created_at
  const dateRequested = get('date_requested');
  let createdAt = null;
  if (dateRequested) {
    const parsed = new Date(dateRequested);
    if (!isNaN(parsed.getTime())) {
      createdAt = parsed.toISOString();
    }
  }

  const smokerVal = parseBooleanField(get('smoker'));

  return attachImportMeta({
    first_name: get('first_name'),
    last_name: get('last_name'),
    phone,
    phone_secondary: phoneSecondary || null,
    email: get('email') || null,
    lead_type: 'aged',
    channel: channel || 'LD',
    lead_source: 'csv_upload',
    lead_vendor: 'AgedLeadStore',
    lead_vendor_id: get('lead_vendor_id') || null,
    status: 'new',
    address: get('address') || null,
    city: get('city') || null,
    state: get('state') || null,
    zip: get('zip') || null,
    area_code: get('area_code') || null,
    time_zone: get('time_zone') || null,
    dob: get('dob') || null,
    gender: get('gender') || null,
    marital_status: get('marital_status') || null,
    smoker: smokerVal,
    height_feet: parseIntField(get('height_feet')),
    height_inches: parseIntField(get('height_inches')),
    weight: parseWeight(get('weight')),
    coverage_amount_requested: get('coverage_amount_requested') || null,
    policy_type_requested: get('policy_type_requested') || null,
    term_requested: get('term_requested') || null,
    metadata: Object.keys(metadata).length > 0 ? metadata : {},
    ...(createdAt ? { created_at: createdAt } : {}),
  }, importMeta);
}

/**
 * Detect if headers match the ProsperityMentors export format.
 */
export function detectProsperityMentorsFormat(headerCells) {
  const normalized = headerCells.map((h) =>
    String(h ?? '').trim().toLowerCase().replace(/\s+/g, '_')
  );
  return normalized.includes('event_title') && normalized.includes('agent_email');
}

/**
 * Map headers for the ProsperityMentors format.
 * @returns {{ fieldToCol: Record<string, number>, unknownHeaders: string[] }}
 */
export function mapProsperityMentorsHeaders(headerCells) {
  const fieldToCol = {};
  const unknownHeaders = [];
  headerCells.forEach((cell, idx) => {
    const normalized = String(cell ?? '').trim().toLowerCase().replace(/\s+/g, '_');
    const mapped = PROSPERITY_MENTORS_HEADERS[normalized];
    if (mapped) {
      if (fieldToCol[mapped] === undefined) fieldToCol[mapped] = idx;
    } else if (String(cell ?? '').trim()) {
      unknownHeaders.push(String(cell).trim());
    }
  });
  return { fieldToCol, unknownHeaders };
}

/**
 * Map a single ProsperityMentors row to a lead.
 */
export function mapProsperityMentorsRowToLead(cells, fieldToCol, importMeta = null) {
  const get = (field) => {
    const col = fieldToCol[field];
    if (col === undefined) return '';
    return String(cells[col] ?? '').trim();
  };

  const fullName = get('name');
  const spaceIdx = fullName.indexOf(' ');
  const firstName = spaceIdx === -1 ? fullName : fullName.slice(0, spaceIdx);
  const lastName = spaceIdx === -1 ? '' : fullName.slice(spaceIdx + 1).trim();

  // captured_date overrides the session imported_at for this field only.
  let capturedAt = null;
  const capturedDate = get('captured_date');
  if (capturedDate) {
    const parsed = new Date(capturedDate);
    if (!isNaN(parsed.getTime())) capturedAt = parsed.toISOString();
  }

  const lead = {
    first_name: firstName,
    last_name: lastName,
    email: get('email') || null,
    phone: get('phone') || null,
    // ProsperityMentors leads default to the IM channel unless the CSV row
    // explicitly provides a channel value.
    channel: get('channel') || 'IM',
    pipeline_stage: get('status') || null,
    payment_status: get('payment_status') || null,
    has_pm_account: parsePmBoolean(get('has_account')),
    lead_source: get('source') || null,
    lead_type: 'event',
    event_title: get('event_title') || null,
    capturing_agent_name: get('agent_name') || null,
    capturing_agent_email: get('agent_email') || null,
    pm_activities: get('activities') || null,
    pm_form_responses: get('form_responses') || null,
    pm_quiz_responses: get('quiz_responses') || null,
    pm_survey_responses: get('survey_responses') || null,
  };

  if (capturedAt) lead.imported_at = capturedAt;

  return attachImportMeta(lead, importMeta);
}

function sanitizeLeadTypeForChannel(rawType, channel, isRecruit) {
  const type = sanitizeLeadType(rawType, isRecruit);
  if (!channel) return type;
  const allowed = leadTypesForChannel(channel);
  if (allowed.includes(type)) return type;
  if (channel === 'LD' && !isRecruit) return 'fresh';
  if (channel === 'IM') return 'f_and_f';
  if (channel === 'AM') return 'life_insurance_agent';
  return allowed[0] || 'fresh';
}

export function mapRowToLead(cells, fieldToCol, isRecruit, channel = 'LD', importMeta = null) {
  const get = (field) => {
    const col = fieldToCol[field];
    if (col === undefined) return '';
    return String(cells[col] ?? '').trim();
  };

  const lead_type = sanitizeLeadTypeForChannel(get('lead_type'), channel, isRecruit);

  return attachImportMeta({
    first_name: get('first_name'),
    last_name: get('last_name'),
    phone: get('phone'),
    email: get('email'),
    lead_type,
    lead_source: sanitizeLeadSource(get('lead_source')),
    channel: channel || null,
    notes: get('notes'),
  }, importMeta);
}

export function rowValidation(lead, isRecruit) {
  const errs = validateLead(lead, isRecruit);
  return { valid: !hasErrors(errs), errors: errs };
}

/**
 * Analyze a CSV file for AgedLeadStore format.
 * @returns {{
 *   ok: boolean,
 *   error?: string,
 *   vendor: 'AgedLeadStore' | 'generic',
 *   rows?: { lead: object, valid: boolean, errors: object, rowNumber: number }[],
 *   validLeads?: object[],
 *   totalDataRows?: number,
 * }}
 */
export function analyzeLeadsCsv(text, isRecruit, { markAgedNonExclusive = true, channel = 'LD', importMeta = null } = {}) {
  const matrix = parseCSV(text);
  if (matrix.length < 2) {
    return { ok: false, error: 'The file must include a header row and at least one data row.' };
  }

  const headerCells = matrix[0];

  if (detectProsperityMentorsFormat(headerCells)) {
    return analyzeProsperityMentorsCsv(matrix, headerCells, importMeta);
  }

  const isAgedLeadStore = detectAgedLeadStoreFormat(headerCells);

  if (isAgedLeadStore) {
    return analyzeAgedLeadStoreCsv(matrix, headerCells, markAgedNonExclusive, channel, importMeta);
  }

  return analyzeGenericCsv(matrix, headerCells, isRecruit, markAgedNonExclusive, channel, importMeta);
}

function analyzeProsperityMentorsCsv(matrix, headerCells, importMeta) {
  const { fieldToCol } = mapProsperityMentorsHeaders(headerCells);

  const dataRows = matrix.slice(1);
  const rows = [];
  const validLeads = [];

  dataRows.forEach((cells, idx) => {
    const rowNumber = idx + 2;
    if (!cells.some((c) => String(c ?? '').trim())) return;

    const lead = mapProsperityMentorsRowToLead(cells, fieldToCol, importMeta);
    const { valid, errors } = rowValidation(lead, false);
    rows.push({ lead, valid, errors, rowNumber });
    if (valid) validLeads.push(lead);
  });

  if (!rows.length) {
    return { ok: false, error: 'No data rows found after the header.' };
  }

  return {
    ok: true,
    vendor: 'ProsperityMentors',
    rows,
    validLeads,
    totalDataRows: rows.length,
  };
}

function finalizeImportLead(lead, markAgedNonExclusive) {
  return applyAgedExclusivity(lead, markAgedNonExclusive);
}

function analyzeAgedLeadStoreCsv(matrix, headerCells, markAgedNonExclusive, channel, importMeta) {
  const { fieldToCol } = mapAgedLeadStoreHeaders(headerCells);

  if (fieldToCol.first_name === undefined || fieldToCol.last_name === undefined) {
    return {
      ok: false,
      error: 'AgedLeadStore format detected but missing first_name or last_name columns.',
    };
  }

  const dataRows = matrix.slice(1);
  const rows = [];
  const validLeads = [];

  dataRows.forEach((cells, idx) => {
    const rowNumber = idx + 2;
    if (!cells.some((c) => String(c ?? '').trim())) return;

    const lead = finalizeImportLead(
      mapAgedLeadStoreRowToLead(cells, fieldToCol, channel, importMeta),
      markAgedNonExclusive,
    );
    const { valid, errors } = rowValidation(lead, false);
    rows.push({ lead, valid, errors, rowNumber });
    if (valid) validLeads.push(lead);
  });

  if (!rows.length) {
    return { ok: false, error: 'No data rows found after the header.' };
  }

  return {
    ok: true,
    vendor: 'AgedLeadStore',
    rows,
    validLeads,
    totalDataRows: rows.length,
  };
}

function analyzeGenericCsv(matrix, headerCells, isRecruit, markAgedNonExclusive, channel, importMeta) {
  const { fieldToCol } = mapCsvHeaders(headerCells);

  if (fieldToCol.first_name === undefined || fieldToCol.last_name === undefined) {
    return {
      ok: false,
      error:
        'Missing required columns. Your CSV must include headers for first name and last name (e.g. first_name, last_name, or "First Name").',
    };
  }

  const dataRows = matrix.slice(1);
  const rows = [];
  const validLeads = [];

  dataRows.forEach((cells, idx) => {
    const rowNumber = idx + 2;
    if (!cells.some((c) => String(c ?? '').trim())) return;

    const lead = finalizeImportLead(mapRowToLead(cells, fieldToCol, isRecruit, channel, importMeta), markAgedNonExclusive);
    const { valid, errors } = rowValidation(lead, isRecruit);
    rows.push({ lead, valid, errors, rowNumber });
    if (valid) validLeads.push(lead);
  });

  if (!rows.length) {
    return { ok: false, error: 'No data rows found after the header.' };
  }

  return {
    ok: true,
    vendor: 'generic',
    rows,
    validLeads,
    totalDataRows: rows.length,
  };
}
